import { useCallback, useEffect, useRef, useState } from 'react'

export interface VoiceRecorderState {
  isRecording: boolean
  isTranscribing: boolean
  error: string | null
}

export interface VoiceRecorder extends VoiceRecorderState {
  toggle: () => Promise<void>
  supported: boolean
}

interface TranscribeResponse {
  success?: boolean
  transcript?: string
  error?: string
}

/**
 * Mic → Deepgram voice input. Wraps MediaRecorder and posts the blob to
 * /api/transcribe. On success the transcript is handed to `onTranscript`;
 * callers decide whether to append, replace, or submit.
 *
 * Cleans up the media stream on unmount or error so the browser's recording
 * indicator doesn't linger.
 */
export function useVoiceRecorder(onTranscript: (text: string) => void): VoiceRecorder {
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const onTranscriptRef = useRef(onTranscript)
  onTranscriptRef.current = onTranscript

  const supported =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia

  const cleanup = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    recorderRef.current = null
  }, [])

  useEffect(() => cleanup, [cleanup])

  const toggle = useCallback(async () => {
    if (!supported) {
      setError('Voice recording is not supported in this browser.')
      return
    }
    setError(null)

    // Stop in-flight recording.
    if (isRecording) {
      const rec = recorderRef.current
      if (rec && rec.state !== 'inactive') rec.stop()
      setIsRecording(false)
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const recorder = new MediaRecorder(stream)
      recorderRef.current = recorder

      const chunks: Blob[] = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data)
      }
      recorder.onstop = async () => {
        cleanup()
        if (chunks.length === 0) return
        setIsTranscribing(true)
        try {
          const blob = new Blob(chunks, { type: 'audio/webm' })
          const form = new FormData()
          form.append('audio', blob, 'recording.webm')
          const res = await fetch('/api/transcribe', {
            method: 'POST',
            credentials: 'include',
            body: form,
          })
          const data: TranscribeResponse = await res.json().catch(() => ({}))
          if (!res.ok || !data.success || !data.transcript) {
            throw new Error(data.error || 'Transcription failed')
          }
          onTranscriptRef.current(data.transcript)
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Transcription failed')
        } finally {
          setIsTranscribing(false)
        }
      }
      recorder.start()
      setIsRecording(true)
    } catch {
      cleanup()
      setError('Microphone access denied')
      setIsRecording(false)
    }
  }, [cleanup, isRecording, supported])

  return { isRecording, isTranscribing, error, toggle, supported }
}
